import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDebounce, DEFAULT_DEBOUNCE_DELAY } from '@/utils/debounce';
import {
  ChevronDown,
  ChevronUp,
  Search,
  Calendar,
  FileText,
  Loader2,
  AlertCircle,
  Download,
  Filter,
  FileDown,
} from 'lucide-react';
import { auditTrailService } from '@/services/audit-trail.service';
import { exportService } from '@/services/export.service';
import SimpleSelect from '@/components/ui/SimpleSelect';
import Pagination from '@/components/ui/Pagination';
import { TableSkeleton } from '@/components/ui/SkeletonLoader';
import { toast } from '@/components/ui/Toast';
import { formatDate, formatDateTime } from '@/utils/dateFormat';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { AUDIT_TRAIL_PERMISSION } from '@/constants/constant-permissions';
import type {
  AuditTrailReport as AuditTrailReportType,
  AuditTrailFilterParams,
  EventTypeOption,
} from '@/types/audit-trail.types';

/**
 * Get today's date in YYYY-MM-DD format
 */
function getTodayDate(): string {
  return new Date().toISOString().split('T')[0];
}

/**
 * Custom display names for event types whose default Title-Case conversion would
 * leak technical / DB-level terminology. Falls back to the auto-formatter when not listed.
 * - GROUP_* uses "Department" because the admin UI surfaces Groups as Departments.
 * - ROLE_PERMISSION_ASSIGNED reads naturally as "Permission Assigned To Role" — matches
 *   the Notes column terminology so Event Type and Notes stay consistent.
 */
const EVENT_TYPE_DISPLAY_OVERRIDES: Record<string, string> = {
  GROUP_CREATED: 'Department Created',
  GROUP_UPDATED: 'Department Updated',
  GROUP_DELETED: 'Department Deleted',
  GROUP_ROLE_PERMISSION_CREATED: 'Role Assigned To Department',
  GROUP_ROLE_PERMISSION_UPDATED: 'Department Role Permissions Updated',
  GROUP_ROLE_PERMISSION_DELETED: 'Role Removed From Department',
  ROLE_PERMISSION_ASSIGNED: 'Permission Assigned To Role',
};

/**
 * Format an audit event_type for display: "USER_DELETED" -> "User Deleted".
 * Honors EVENT_TYPE_DISPLAY_OVERRIDES first so domain-specific names take precedence.
 */
function formatEventType(eventType: string | undefined | null): string {
  if (!eventType) return '';
  if (EVENT_TYPE_DISPLAY_OVERRIDES[eventType]) return EVENT_TYPE_DISPLAY_OVERRIDES[eventType];
  return eventType
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Internal fields that always change or have no business value (Mongo internals, timestamps,
 * credentials). Hidden from the diff so the JSON view only shows fields the admin actually edited.
 */
const HIDDEN_DIFF_FIELDS = new Set([
  '_id', 'id', '__v', 'createdAt', 'updatedAt', 'created_at', 'updated_at',
  'created_by', 'updated_by', 'is_deleted', 'tenant_key',
  'password_hash', 'access_token', 'refresh_token', 'invitation_token',
]);

/**
 * Tokenize a string for word-level diff. Splits on non-word boundaries while
 * preserving the separators, so the diff works for both plain prose AND
 * serialized JSON (objects/arrays). Each meaningful word inside the JSON
 * becomes its own token, so a single value change inside a long stringified
 * array only highlights the changed token instead of the entire JSON blob.
 */
function tokenizeForDiff(text: string): string[] {
  return text.split(/(\W+)/).filter((t) => t !== '');
}

/**
 * Compute a word-level diff between two strings using the classic LCS algorithm.
 * Returns an ordered list of parts: each part is either common (in both), removed
 * (only in old), or added (only in new). The audit JSON view uses this so a one-word
 * edit highlights just that word — not the entire paragraph — making it easy to spot
 * what actually changed in long text fields (course descriptions, etc.).
 */
type DiffPart = { type: 'same' | 'removed' | 'added'; text: string };
function computeWordDiff(oldStr: string, newStr: string): DiffPart[] {
  const oldTokens = tokenizeForDiff(oldStr);
  const newTokens = tokenizeForDiff(newStr);
  const m = oldTokens.length;
  const n = newTokens.length;

  // LCS length table
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (oldTokens[i - 1] === newTokens[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  // Backtrack to build the ordered diff
  const result: DiffPart[] = [];
  let i = m;
  let j = n;
  while (i > 0 && j > 0) {
    if (oldTokens[i - 1] === newTokens[j - 1]) {
      result.unshift({ type: 'same', text: oldTokens[i - 1] });
      i--;
      j--;
    } else if (dp[i - 1][j] >= dp[i][j - 1]) {
      result.unshift({ type: 'removed', text: oldTokens[i - 1] });
      i--;
    } else {
      result.unshift({ type: 'added', text: newTokens[j - 1] });
      j--;
    }
  }
  while (i > 0) {
    result.unshift({ type: 'removed', text: oldTokens[i - 1] });
    i--;
  }
  while (j > 0) {
    result.unshift({ type: 'added', text: newTokens[j - 1] });
    j--;
  }

  return result;
}

/**
 * Render a single side (old or new) of a word-level diff. Only the tokens that
 * actually changed get the red/green highlight; common tokens stay unstyled so the
 * eye lands on the diff instead of scanning a wall of color.
 */
interface WordDiffSideProps {
  oldText: string;
  newText: string;
  side: 'old' | 'new';
}
function WordDiffSide({ oldText, newText, side }: WordDiffSideProps) {
  const diff = useMemo(() => computeWordDiff(oldText, newText), [oldText, newText]);
  return (
    <>
      {diff.map((part, idx) => {
        if (part.type === 'same') {
          return <span key={idx}>{part.text}</span>;
        }
        if (side === 'old' && part.type === 'removed') {
          return <span key={idx} className="bg-red-200 text-red-800">{part.text}</span>;
        }
        if (side === 'new' && part.type === 'added') {
          return <span key={idx} className="bg-green-200 text-green-800">{part.text}</span>;
        }
        return null;
      })}
    </>
  );
}

/**
 * JSON Diff Component — same JSON look as before, but filtered to ONLY show fields whose
 * values changed between old and new. Unchanged fields and internal/timestamp fields are
 * dropped so the audit clearly answers: which fields were updated, previous value, updated value.
 *
 * For STRING values that changed, a word-level diff narrows the highlight to just the
 * changed words instead of painting the whole value red/green.
 */
interface JsonDiffProps {
  oldData: Record<string, unknown> | undefined;
  newData: Record<string, unknown> | undefined;
}

function JsonDiff({ oldData, newData }: JsonDiffProps) {
  const hasChanged = (key: string): boolean => {
    const oldVal = oldData?.[key];
    const newVal = newData?.[key];
    return JSON.stringify(oldVal) !== JSON.stringify(newVal);
  };

  // Only the keys whose values actually changed (ignoring internal/timestamp fields).
  // Unchanged fields are dropped so the JSON view focuses on what the admin actually edited.
  const allKeys = useMemo(() => {
    const keys = new Set<string>();
    if (oldData) Object.keys(oldData).forEach((k) => keys.add(k));
    if (newData) Object.keys(newData).forEach((k) => keys.add(k));
    return Array.from(keys).filter((k) => !HIDDEN_DIFF_FIELDS.has(k) && hasChanged(k));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oldData, newData]);

  const formatValue = (value: unknown): string => {
    if (value === null) return 'null';
    if (value === undefined) return '';
    if (typeof value === 'boolean') return value.toString();
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  // If no old data (CREATE operation), show only the created data
  if (!oldData || Object.keys(oldData).length === 0) {
    const newKeys = newData ? Object.keys(newData).filter((k) => !HIDDEN_DIFF_FIELDS.has(k)) : [];
    return (
      <div className="mt-4">
        <div className="border border-gray-300 rounded-lg overflow-hidden">
          <div className="bg-green-100 px-4 py-2 border-b border-gray-300">
            <h4 className="font-semibold text-gray-800">Created data:</h4>
          </div>
          <div className="p-4 bg-white max-h-96 overflow-auto">
            <pre className="text-sm font-mono whitespace-pre-wrap text-gray-700">
              {'{'}
              {newKeys.map((key, index) => {
                const value = newData?.[key];
                const formattedValue = formatValue(value);
                const isString = typeof value === 'string';
                return (
                  <div key={key} className="ml-2">
                    <span className="text-gray-700">"{key}"</span>
                    <span className="text-gray-500">: </span>
                    <span className="text-green-700">
                      {isString ? `"${formattedValue}"` : formattedValue}
                    </span>
                    {index < newKeys.length - 1 && <span className="text-gray-500">,</span>}
                  </div>
                );
              })}
              {'}'}
            </pre>
          </div>
        </div>
      </div>
    );
  }

  // No changed fields after filtering — nothing meaningful to show.
  if (allKeys.length === 0) {
    return (
      <div className="mt-4 text-sm text-gray-500 italic">
        No field-level changes detected.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
      {/* Old Data */}
      <div className="border border-gray-300 rounded-lg overflow-hidden">
        <div className="bg-gray-100 px-4 py-2 border-b border-gray-300">
          <h4 className="font-semibold text-gray-800">Old data:</h4>
        </div>
        <div className="p-4 bg-white max-h-96 overflow-auto">
          <pre className="text-sm font-mono whitespace-pre-wrap text-gray-700">
            {'{'}
            {allKeys.map((key, index) => {
              const value = oldData?.[key];
              const newValue = newData?.[key];
              const formattedValue = formatValue(value);
              const newFormattedValue = formatValue(newValue);
              const isString = typeof value === 'string';
              const changed = hasChanged(key);
              // Apply word-level diff for any changed value (string OR serialized object/array)
              // so that long JSON values only highlight the actually-different tokens.
              const useWordDiff = changed && value !== undefined && newValue !== undefined;
              return (
                <div key={key} className="ml-2">
                  <span className="text-gray-700">"{key}"</span>
                  <span className="text-gray-500">: </span>
                  {useWordDiff ? (
                    <span className="text-gray-700">
                      {isString && '"'}
                      <WordDiffSide oldText={formattedValue} newText={newFormattedValue} side="old" />
                      {isString && '"'}
                    </span>
                  ) : (
                    <span className={changed ? "bg-red-200 text-red-800" : "text-gray-700"}>
                      {isString ? `"${formattedValue}"` : formattedValue}
                    </span>
                  )}
                  {index < allKeys.length - 1 && <span className="text-gray-500">,</span>}
                </div>
              );
            })}
            {'}'}
          </pre>
        </div>
      </div>

      {/* New Data - Shows inline diff */}
      <div className="border border-gray-300 rounded-lg overflow-hidden">
        <div className="bg-gray-100 px-4 py-2 border-b border-gray-300">
          <h4 className="font-semibold text-gray-800">New data:</h4>
        </div>
        <div className="p-4 bg-white max-h-96 overflow-auto">
          <pre className="text-sm font-mono whitespace-pre-wrap text-gray-700">
            {'{'}
            {allKeys.map((key, index) => {
              const oldValue = oldData?.[key];
              const newValue = newData?.[key];
              const changed = hasChanged(key);
              const oldFormatted = formatValue(oldValue);
              const newFormatted = formatValue(newValue);
              const isNewString = typeof newValue === 'string';
              // Apply word-level diff for any changed value so a single-token change inside
              // a long serialized JSON value only highlights that token, not the whole blob.
              const useWordDiff = changed && oldValue !== undefined && newValue !== undefined;

              return (
                <div key={key} className="ml-2">
                  <span className="text-gray-700">"{key}"</span>
                  <span className="text-gray-500">: </span>
                  {useWordDiff ? (
                    <span className="text-gray-700">
                      {isNewString && '"'}
                      <WordDiffSide oldText={oldFormatted} newText={newFormatted} side="new" />
                      {isNewString && '"'}
                    </span>
                  ) : changed ? (
                    <span className="bg-green-200 text-green-800">
                      {isNewString ? `"${newFormatted}"` : newFormatted}
                    </span>
                  ) : (
                    <span className="text-gray-700">
                      {isNewString ? `${newFormatted}` : newFormatted}
                    </span>
                  )}
                  {index < allKeys.length - 1 && <span className="text-gray-500">,</span>}
                </div>
              );
            })}
            {'}'}
          </pre>
        </div>
      </div>
    </div>
  );
}

const PREVIEW_LIMIT = 3;

/**
 * Expandable Row Component
 */
interface ExpandableRowProps {
  report: AuditTrailReportType;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
}

function ExpandableRow({ report: reportProp, index, isExpanded, onToggle }: ExpandableRowProps) {
  const [showAllRecipients, setShowAllRecipients] = useState(false);
  const [showNoteExpanded, setShowNoteExpanded] = useState(false);


  const isArchived = reportProp.from_archive === true;
  const [archivedDetail, setArchivedDetail] = useState<AuditTrailReportType | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);

  useEffect(() => {
    if (isExpanded && isArchived && !archivedDetail && !isLoadingDetail) {
      setIsLoadingDetail(true);
      auditTrailService
        .getAuditTrailReportById(reportProp._id)
        .then((full) => setArchivedDetail(full))
        .catch(() => { /* keep the light row; detail simply won't show */ })
        .finally(() => setIsLoadingDetail(false));
    }
  }, [isExpanded, isArchived, archivedDetail, isLoadingDetail, reportProp._id]);


  const report = archivedDetail ?? reportProp;

  const browserName = report.device_info?.browser?.name || report.browser;
  const osName = report.device_info?.os?.name || report.device;
  const ipAddress = report.ip_address || report.data?.ip_address;
  const oldData = report.old_data || report.data?.old_data;
  const newData = report.new_data || report.data?.new_data;
  const hasOldNewData = oldData || newData;

  // Classify event type to drive sender/recipient display
  const isAutoEvent = report.event_type.endsWith('_AUTO_SENT');
  const isScheduledEvent = report.event_type.includes('SCHEDULED');
  const isSendEvent = isAutoEvent ||
    isScheduledEvent ||
    report.event_type.includes('NOTIFICATION_SENT') ||
    report.event_type.includes('REMINDER') ||
    report.event_type.includes('INVITATION');

  // Labels — scheduled events use "Scheduled By/To" for clarity
  const senderLabel = isScheduledEvent ? 'Scheduled By' : 'Sent By';
  const recipientLabel = isScheduledEvent ? 'Scheduled To' : 'Sent To';

  // Sender resolution
  // AUTO events (scheduler): sender is always "System"
  // Admin-triggered events with data.sender_*: use those fields
  // Other send events (manual reminder, invitation): fall back to action_by_info
  const hasSenderData = !!(report.data?.sender_user_id || report.data?.sender_name || report.data?.sender_email);
  let senderName = '';
  let senderEmail = '';
  let hasSender = false;
  if (isAutoEvent) {
    senderName = 'System';
    hasSender = true;
  } else if (hasSenderData) {
    senderName = (report.data?.sender_name as string) || report.action_by_info?.name || '';
    senderEmail = (report.data?.sender_email as string) || report.action_by_info?.email || '';
    hasSender = true;
  } else if (isSendEvent) {
    senderName = report.action_by_info?.name || '';
    senderEmail = report.action_by_info?.email || '';
    hasSender = !!(senderName || senderEmail);
  }

  // Recipient resolution — check all possible locations the SDK may store recipient info.
  // Never use action_by_info for recipient; for AUTO events action_by = SYSTEM_SCHEDULER.
  const recipientName = (report.data?.recipient_name as string) ||
    (isSendEvent ? (report.user_info?.name || (report.meta_data?.user_name as string) || '') : '');
  const recipientEmail = (report.data?.recipient_email as string) ||
    (isSendEvent ? (
      (report.data?.triggered_by_email as string) ||
      report.user_info?.email ||
      (report.meta_data?.user_email as string) ||
      ''
    ) : '');
  const hasRecipient = !!(recipientName || recipientEmail);

  // Multi-recipient pills (GROUP/ROLE assignments)
  const recipientDetails = report.data?.recipient_details || [];
  const recipientCount = report.data?.recipient_count ?? 0;
  const showRecipientCount = recipientCount > 0 && !hasRecipient && recipientDetails.length === 0;
  const showRecipientPills = recipientDetails.length > 1 || (recipientDetails.length > 0 && !hasRecipient);
  const visibleRecipients = showAllRecipients ? recipientDetails : recipientDetails.slice(0, PREVIEW_LIMIT);
  const hasMoreStored = !showAllRecipients && recipientDetails.length > PREVIEW_LIMIT;

  // Resource names for rich note (course / certification / learning path)
  const resourceNames = report.data?.course_names || report.data?.certification_names || report.data?.learning_path_names || [];

  // Generic actor info: only shown for non-send events (login, update, etc.).
  // IMPORTANT: action_by_info is the *actor* (resolved from action_by user_id via the SDK's
  // lookupUserInfo). Prefer it over data.email / meta_data.user_email — for USER_CREATED those
  // fields hold the NEW user's email, which would incorrectly show as "Created By".
  const performedByName = !hasSender
    ? (report.action_by_info?.name || report.meta_data?.user_name || report.user_info?.name)
    : null;
  const performedByEmail = !hasSender
    ? (report.action_by_info?.email || report.meta_data?.user_email || report.data?.email)
    : null;

  // Friendlier actor label based on event type so the user can see Updated By / Created By /
  // Deleted By / Restored By instead of a generic "Performed By" for every event.
  const isUpdateEvent = report.event_type.endsWith('_UPDATED');
  const isCreateEvent = report.event_type.endsWith('_CREATED');
  const isDeleteEvent = report.event_type.endsWith('_DELETED');
  const isRestoreEvent = report.event_type.endsWith('_RESTORED');
  const actorLabel = isUpdateEvent
    ? 'Updated By'
    : isCreateEvent
      ? 'Created By'
      : isDeleteEvent
        ? 'Deleted By'
        : isRestoreEvent
          ? 'Restored By'
          : 'Performed By';

  // Failure reason — for *_FAILED events OR partial-failure events like
  // BULK_USER_IMPORT_COMPLETED where some rows failed but the overall job finished.
  // Falls back to legacy field names from older audit records.
  const isFailedEvent = report.event_type.includes('FAILED') || report.event_type.includes('FAILURE');
  const failureReason =
    (report.data?.failure_reason as string) ||
    (report.data?.reason as string) ||
    (report.data?.error as string) ||
    (report.data?.error_description as string) ||
    (report.data?.new_data?.reason as string) ||
    '';

  // Rich note: "Assigned [Course Name] to [User Name]" when names are available
  const rawNote = report.note || report.event_description;
  // For CRUD events (USER_CREATED, ROLE_UPDATED, etc.), append the resource name so the note
  // reads "User created: John Smith" instead of just "User created".
  const enrichedRawNote = (() => {
    if (!rawNote) return rawNote;
    if (isSendEvent || isFailedEvent) return rawNote;
    const resourceName = report.data?.resource_name as string;
    if (resourceName && !rawNote.toLowerCase().includes(resourceName.toLowerCase())) {
      return `${rawNote}: ${resourceName}`;
    }
    return rawNote;
  })();
  const noteNode: React.ReactNode = (() => {
    if (resourceNames.length === 0) return enrichedRawNote || null;
    const resourceText = resourceNames.slice(0, 3).join(', ') + (resourceNames.length > 3 ? ` +${resourceNames.length - 3} more` : '');

    if (recipientName) {
      return `Assigned ${resourceText} to ${recipientName}`;
    }

    if (recipientDetails.length > 0) {
      const shownNames = recipientDetails.slice(0, 2).map(r => r.name || r.email).join(', ');
      const remaining = recipientDetails.length - 2;
      if (remaining > 0) {
        if (showNoteExpanded) {
          const allNames = recipientDetails.map(r => r.name || r.email).join(', ');
          return (
            <>
              {`Assigned ${resourceText} to ${allNames} `}
              <span className="text-gray-400"> · </span>
              <button
                onClick={(e) => { e.stopPropagation(); setShowNoteExpanded(false); }}
                className="text-blue-600 hover:underline font-medium"
              >
                Show less
              </button>
            </>
          );
        }
        return (
          <>
            {`Assigned ${resourceText} to ${shownNames} `}
            <button
              onClick={(e) => { e.stopPropagation(); setShowNoteExpanded(true); }}
              className="text-blue-600 hover:underline font-medium"
            >
              +{remaining} more
            </button>
          </>
        );
      }
      return `Assigned ${resourceText} to ${shownNames}`;
    }

    if (recipientCount > 0) {
      return `Assigned ${resourceText} to ${recipientCount} user${recipientCount > 1 ? 's' : ''}`;
    }

    return enrichedRawNote || null;
  })();

  return (
    <>
      <tr
        className={`border-b border-gray-200 cursor-pointer transition-colors ${
          isExpanded ? 'bg-gray-50' : 'hover:bg-gray-50'
        }`}
        onClick={onToggle}
      >
        <td className="px-6 py-4 text-sm text-gray-600 w-16">{index + 1}</td>
        <td className="px-6 py-4">
          <span className="text-sm font-medium text-gray-700">
            {formatEventType(report.event_type)}
          </span>
          {isArchived && (
            <span className="ml-2 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500 align-middle">
              Archived
            </span>
          )}
        </td>
        <td className="px-6 py-4 text-sm text-gray-600">{formatDateTime(report.event_time)}</td>
        <td className="px-6 py-4 text-right w-20">
          <button
            className="p-1 rounded-full hover:bg-gray-200 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
          >
            {isExpanded ? (
              <ChevronUp className="w-5 h-5 text-gray-600" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-600" />
            )}
          </button>
        </td>
      </tr>

      {isExpanded && (
        <tr className="bg-white border-b border-gray-200">
          <td colSpan={4} className="px-6 py-5">
            <div className="space-y-4">
              {isArchived && isLoadingDetail && !archivedDetail && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading archived details…
                </div>
              )}
              <div className="space-y-2">
                {noteNode && (
                  <div className="flex">
                    <span className="font-semibold text-gray-700 w-32 shrink-0">Note:</span>
                    <span className="text-gray-800">{noteNode}</span>
                  </div>
                )}
                {failureReason && (
                  <div className="flex">
                    <span className="font-semibold text-gray-700 w-32 shrink-0">Failure Reason:</span>
                    <span className="text-red-700">{failureReason}</span>
                  </div>
                )}
                {(performedByName || performedByEmail) && (
                  <div className="flex">
                    <span className="font-semibold text-gray-700 w-32 shrink-0">{actorLabel}:</span>
                    <span className="text-gray-800">
                      {[performedByName, performedByEmail && `(${performedByEmail})`].filter(Boolean).join(' ')}
                    </span>
                  </div>
                )}
                {browserName && (
                  <div className="flex">
                    <span className="font-semibold text-gray-700 w-32 shrink-0">Browser:</span>
                    <span className="text-gray-800">{browserName}</span>
                  </div>
                )}
                {osName && (
                  <div className="flex">
                    <span className="font-semibold text-gray-700 w-32 shrink-0">Device:</span>
                    <span className="text-gray-800">{osName}</span>
                  </div>
                )}
                {ipAddress && (
                  <div className="flex">
                    <span className="font-semibold text-gray-700 w-32 shrink-0">Browser IP:</span>
                    <span className="text-gray-800">{ipAddress}</span>
                  </div>
                )}
                {hasSender && (
                  <div className="flex">
                    <span className="font-semibold text-gray-700 w-32 shrink-0">{senderLabel}:</span>
                    <span className="text-gray-800">
                      {[senderName, senderEmail && `(${senderEmail})`].filter(Boolean).join(' ')}
                    </span>
                  </div>
                )}
                {hasRecipient && (
                  <div className="flex">
                    <span className="font-semibold text-gray-700 w-32 shrink-0">{recipientLabel}:</span>
                    <span className="text-gray-800">
                      {[recipientName, recipientEmail && `(${recipientEmail})`].filter(Boolean).join(' ')}
                    </span>
                  </div>
                )}
                {showRecipientPills && (
                  <div className="flex items-start">
                    <span className="font-semibold text-gray-700 w-32 shrink-0 pt-0.5">Recipients:</span>
                    <div className="flex flex-wrap gap-1 items-center">
                      {visibleRecipients.map((r, i) => (
                        <span key={i} className="inline-flex items-center gap-1 bg-green-50 text-green-700 text-xs font-medium px-2 py-0.5 rounded border border-green-200">
                          <span>{r.name || r.email}</span>
                          {r.name && r.email && (
                            <span className="text-green-500 font-normal">({r.email})</span>
                          )}
                        </span>
                      ))}
                      {hasMoreStored && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setShowAllRecipients(true); }}
                          className="text-xs text-blue-600 hover:underline font-medium"
                        >
                          +{recipientDetails.length - PREVIEW_LIMIT} more
                        </button>
                      )}
                      {showAllRecipients && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setShowAllRecipients(false); }}
                          className="text-xs text-blue-600 hover:underline font-medium"
                        >
                          Show less
                        </button>
                      )}
                    </div>
                  </div>
                )}
                {showRecipientCount && (
                  <div className="flex">
                    <span className="font-semibold text-gray-700 w-32 shrink-0">Recipients:</span>
                    <span className="text-gray-800">
                      {recipientCount === 1 ? '1 user' : `${recipientCount} users`}
                    </span>
                  </div>
                )}
              </div>

              {hasOldNewData && (
                <JsonDiff
                  oldData={oldData as Record<string, unknown> | undefined}
                  newData={newData as Record<string, unknown> | undefined}
                />
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Main Audit Trail Report Page Component
 */
export default function AuditTrailReport() {
  usePermissionRouteAccess([
    AUDIT_TRAIL_PERMISSION.ADMIN_AUDIT_TRAIL_READ,
    AUDIT_TRAIL_PERMISSION.ADMIN_AUDIT_TRAIL_WRITE,
  ]);
  const canWrite = useHasPermission([AUDIT_TRAIL_PERMISSION.ADMIN_AUDIT_TRAIL_WRITE]);

  const todayDate = getTodayDate();

  const [eventType, setEventType] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [includeArchived, setIncludeArchived] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const debouncedSearch = useDebounce(search, DEFAULT_DEBOUNCE_DELAY);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(20);

  const handlePageSizeChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [exportLoading, setExportLoading] = useState<{ excel: boolean; csv: boolean }>({ excel: false, csv: false });
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const startDateRef = useRef<HTMLInputElement>(null);
  const endDateRef = useRef<HTMLInputElement>(null);

  // Reset to page 1 when debounced search changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const filterParams: AuditTrailFilterParams = useMemo(() => {
    const params: AuditTrailFilterParams = {
      skip: (page - 1) * limit,
      limit,
    };
    if (eventType) params.event_type = eventType;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
    if (includeArchived) params.include_archived = true;
    return params;
  }, [eventType, startDate, endDate, debouncedSearch, includeArchived, page, limit]);

  const { data: eventOptions = [] } = useQuery({
    queryKey: ['audit-trail', 'event-dropdown'],
    queryFn: () => auditTrailService.getEventDropdownOptions(),
  });

  const {
    data: reportData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['audit-trail', 'list', filterParams],
    queryFn: () => auditTrailService.getAuditTrailReports(filterParams),
  });

  const handleSearch = () => {
    setPage(1);
    refetch();
  };

  const handleClear = () => {
    setEventType('');
    setStartDate('');
    setEndDate('');
    setIncludeArchived(false);
    setSearch('');
    setPage(1);
  };

  const handleExport = async (format: 'csv' | 'excel') => {
    try {
      setExportLoading(prev => ({ ...prev, [format]: true }));

      const filters = {
        ...(eventType && { event_type: eventType }),
        ...(startDate && { start_date: startDate }),
        ...(endDate && { end_date: endDate }),
        ...(debouncedSearch.trim() && { search: debouncedSearch.trim() }),
        ...(includeArchived && { include_archived: true }),
      };

      const result = await exportService.exportAuditTrail(format, filters);

      if (!result.success) {
        toast.error(result.message || 'No data available to export');
        return;
      }

      toast.success(
        result.count >= 0
          ? `${result.count} audit trail records exported successfully`
          : `Audit trail exported successfully (${result.filename})`
      );
    } catch (error) {
      console.error(`Audit trail ${format} export failed:`, error);
      const message = error instanceof Error ? error.message : '';
      toast.error(`Failed to export audit trail as ${format.toUpperCase()}${message ? `: ${message}` : ''}`);
    } finally {
      setExportLoading(prev => ({ ...prev, [format]: false }));
    }
  };

  const handleStartDateChange = (value: string) => {
    const dateOnly = value.split('T')[0];
    setStartDate(dateOnly);
    // If end date is before start date, update end date
    if (endDate && dateOnly > endDate) {
      setEndDate(dateOnly);
    }
  };

  const handleEndDateChange = (value: string) => {
    const dateOnly = value.split('T')[0];
    // Don't allow end date after today
    if (dateOnly > todayDate) {
      setEndDate(todayDate);
    } else {
      setEndDate(dateOnly);
    }
  };

  const toggleRow = (reportId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(reportId)) {
        newSet.delete(reportId);
      } else {
        newSet.add(reportId);
      }
      return newSet;
    });
  };

  const totalCount = reportData?.total_count || 0;
  const totalPages = Math.ceil(totalCount / limit);
  const reports = reportData?.audit_trail_reports || [];

  return (
    <div className="space-y-6">
      {/* Header - Blue Gradient like other pages */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">Audit Trail Report</h1>
            <p className="text-blue-100">
              Track and review all system changes and user activities ({totalCount.toLocaleString()} total records)
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                showFilters
                  ? 'bg-white text-blue-600'
                  : 'bg-white/20 backdrop-blur-sm hover:bg-white/30'
              }`}
            >
              <Filter className="w-5 h-5" />
              <span className="hidden sm:inline">Filters</span>
            </button>
            <button
              onClick={() => canWrite && handleExport('excel')}
              disabled={!canWrite || exportLoading.excel || reports.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={!canWrite ? 'You do not have permission' : 'Export as Excel'}
            >
              {exportLoading.excel ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <FileDown className="w-5 h-5" />
              )}
              <span className="hidden sm:inline">
                {exportLoading.excel ? 'Exporting...' : 'Excel'}
              </span>
            </button>
            <button
              onClick={() => canWrite && handleExport('csv')}
              disabled={!canWrite || exportLoading.csv || reports.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm hover:bg-white/30 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={!canWrite ? 'You do not have permission' : 'Export as CSV'}
            >
              {exportLoading.csv ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Download className="w-5 h-5" />
              )}
              <span className="hidden sm:inline">
                {exportLoading.csv ? 'Exporting...' : 'CSV'}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
            <div className="flex items-center gap-3">
              <button
                onClick={handleSearch}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 text-sm font-medium"
              >
                <Search className="w-4 h-4" />
                Apply Filters
              </button>
              <button
                onClick={handleClear}
                className="text-sm text-gray-600 hover:text-gray-800"
              >
                Clear All
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Event Type Dropdown */}
            <div>
              <SimpleSelect
                label="Event type"
                value={eventType}
                onChange={(value) => setEventType(value as string || '')}
                options={[
                  { label: 'All event types', value: '' },
                  ...eventOptions.map((option: EventTypeOption) => ({
                    label: option.label,
                    value: option.value,
                  })),
                ]}
                placeholder="Select event type"
                isClearable
              />
            </div>

            {/* Search by username or email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">User</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 z-10" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                  placeholder="Search by username or email"
                  className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start date</label>
              <div
                className="relative cursor-pointer"
                onClick={() => startDateRef.current?.showPicker()}
              >
                <div className="flex items-center gap-2 px-3 py-2.5 border border-gray-300 rounded-lg bg-white text-sm focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                  <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className={startDate ? 'text-gray-900' : 'text-gray-400'}>
                    {startDate ? formatDate(startDate, 'dd/mm/yyyy') : 'dd/mm/yyyy'}
                  </span>
                </div>
                <input
                  ref={startDateRef}
                  type="date"
                  value={startDate}
                  onChange={(e) => handleStartDateChange(e.target.value)}
                  max={todayDate}
                  className="absolute inset-0 w-full h-full opacity-0 pointer-events-none"
                />
              </div>
            </div>

            {/* End Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End date</label>
              <div
                className="relative cursor-pointer"
                onClick={() => endDateRef.current?.showPicker()}
              >
                <div className="flex items-center gap-2 px-3 py-2.5 border border-gray-300 rounded-lg bg-white text-sm focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                  <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className={endDate ? 'text-gray-900' : 'text-gray-400'}>
                    {endDate ? formatDate(endDate, 'dd/mm/yyyy') : 'dd/mm/yyyy'}
                  </span>
                </div>
                <input
                  ref={endDateRef}
                  type="date"
                  value={endDate}
                  onChange={(e) => handleEndDateChange(e.target.value)}
                  min={startDate || undefined}
                  max={todayDate}
                  className="absolute inset-0 w-full h-full opacity-0 pointer-events-none"
                />
              </div>
            </div>

            {/* Include archived toggle — Datadog-style "Historical Index" opt-in */}
            <div className="flex items-end">
              <label className="flex items-center gap-2 px-3 py-2.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={includeArchived}
                  onChange={(e) => { setIncludeArchived(e.target.checked); setPage(1); }}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">Include archived logs</span>
                <span className="text-xs text-gray-400">(older, from storage)</span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Table Card */}
      <div className="card overflow-hidden p-0">
        {isLoading ? (
          <TableSkeleton rows={limit} columns={4} />
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-12 text-red-600">
            <AlertCircle className="w-12 h-12 mb-3" />
            <p className="font-medium">Failed to load audit trail</p>
            <p className="text-sm text-gray-500 mt-1">
              {error instanceof Error ? error.message : 'Unknown error occurred'}
            </p>
            <button
              onClick={() => refetch()}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Try Again
            </button>
          </div>
        ) : reports.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500">
            <FileText className="w-12 h-12 mb-3 text-gray-400" />
            <p className="font-medium">No audit trail records found</p>
            <p className="text-sm mt-1">Try adjusting your filters</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">S.NO</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">EVENT TYPE</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">EVENT DATE & TIME</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">ACTION</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((report, index) => (
                <ExpandableRow
                  key={report._id}
                  report={report}
                  index={(page - 1) * limit + index}
                  isExpanded={expandedRows.has(report._id)}
                  onToggle={() => toggleRow(report._id)}
                />
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {reports.length > 0 && (
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={totalCount}
            itemsPerPage={limit}
            onPageChange={setPage}
            onItemsPerPageChange={handlePageSizeChange}
            showItemsPerPage={true}
            itemsPerPageOptions={[10, 20, 50, 100]}
          />
        )}
      </div>
    </div>
  );
}
